(() => {
  if (window.llMaterialVariantFilterCounts) return;
  window.llMaterialVariantFilterCounts = true;

  const COLOR_PARAM = 'filter.p.m.material.color_families';
  const indexCache = new Map();
  let scheduled = false;

  const colorRules = [
    { labels: ['kleurloos', 'colorless'], words: ['kleurloos', 'colorless', 'clear', 'transparant', 'transparent', 'glas', 'glass'] },
    { labels: ['wit', 'white'], words: ['wit', 'white'] },
    { labels: ['zwart', 'black'], words: ['zwart', 'black'] },
    { labels: ['grijs', 'grey'], words: ['grijs', 'grey', 'gray', 'donkergrijs', 'lichtgrijs', 'smoke', 'rookgrijs'] },
    { labels: ['naturel/bruin', 'natural/brown'], words: ['naturel', 'natural', 'bruin', 'brown', 'kraft', 'kurk', 'cork', 'eiken', 'oak', 'mahonie', 'mahogany', 'noten', 'walnut', 'champagne', 'champange'] },
    { labels: ['rood', 'red'], words: ['rood', 'red', 'bordeaux', 'burgundy', 'signaalrood', 'verkeersrood'] },
    { labels: ['oranje', 'orange'], words: ['oranje', 'orange', 'oranjegeel'] },
    { labels: ['geel', 'yellow'], words: ['geel', 'yellow', 'oranjegeel', 'verkeersgeel'] },
    { labels: ['groen', 'green'], words: ['groen', 'green', 'mintgroen', 'geelgroen'] },
    { labels: ['blauw', 'blue'], words: ['blauw', 'blue', 'nachtblauw', 'ultramarijn', 'ultramarine', 'verkeersblauw', 'jeans'] },
    { labels: ['roze', 'pink'], words: ['roze', 'rose', 'pink', 'roségoud', 'rosegoud'] },
    { labels: ['paars', 'purple'], words: ['paars', 'purple', 'violet'] },
    { labels: ['metallic'], words: ['metallic', 'metaal', 'goud', 'gold', 'zilver', 'silver', 'roségoud', 'rosegoud'] },
  ];

  function normalized(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  function asArray(value) {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    return value ? [String(value)] : [];
  }

  function inferCardColors(card, productColors, siblingCount) {
    if (productColors.length <= 1 || siblingCount <= 1) return productColors;

    const title = card.querySelector('.ll-material-card__title')?.textContent || '';
    const source = normalized(title);
    const tokens = new Set(source.split(/[^a-z0-9]+/).filter(Boolean));
    const detectedLabels = new Set();

    colorRules.forEach((rule) => {
      if (source.includes('yellow green') && rule.labels.some((label) => normalized(label) === 'yellow')) return;
      if (rule.words.some((word) => tokens.has(normalized(word)))) {
        rule.labels.forEach((label) => detectedLabels.add(label));
      }
    });

    const detected = productColors.filter((value) => detectedLabels.has(normalized(value)));
    if (detected.length) return detected;

    card.dataset.llColorInferenceFallback = 'true';
    return [];
  }

  function cardsFromDocument(root) {
    const cards = [];
    root.querySelectorAll('[data-material-card-pages]').forEach((grid) => {
      let productCards = [];
      Array.from(grid.children).forEach((node) => {
        if (node.matches('.ll-material-card')) {
          productCards.push(node);
          return;
        }
        if (!node.matches('[data-ll-material-filter-record]')) return;

        try {
          const record = JSON.parse(node.textContent);
          const productFilters = {};
          Object.entries(record.filters || {}).forEach(([param, values]) => { productFilters[param] = asArray(values); });
          productCards.forEach((element) => {
            const filters = {};
            Object.entries(productFilters).forEach(([param, values]) => { filters[param] = [...values]; });
            filters[COLOR_PARAM] = inferCardColors(element, filters[COLOR_PARAM] || [], productCards.length);
            cards.push({ element, filters, handle: record.handle });
          });
        } catch (error) {
          console.error('Invalid material filter record', error);
        }
        productCards = [];
      });
    });
    return cards;
  }

  async function fetchDocument(url, attempt = 0) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (response.status === 429 && attempt < 3) {
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
      return fetchDocument(url, attempt + 1);
    }
    if (!response.ok) throw new Error(`Material filter index request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function pageCount(doc) {
    let pages = 1;
    doc.querySelectorAll('.pagination a[href]').forEach((link) => {
      const page = Number(new URL(link.href, location.origin).searchParams.get('page'));
      if (Number.isFinite(page)) pages = Math.max(pages, page);
    });
    return pages;
  }

  async function loadCollectionIndex() {
    const key = `${document.documentElement.lang}|${location.pathname}`;
    if (indexCache.has(key)) return indexCache.get(key);

    const promise = (async () => {
      const base = new URL(location.pathname, location.origin);
      base.searchParams.set('page', '1');
      const first = await fetchDocument(base);
      const documents = [first];
      const totalPages = pageCount(first);
      for (let page = 2; page <= totalPages; page += 1) {
        const pageUrl = new URL(base);
        pageUrl.searchParams.set('page', String(page));
        documents.push(await fetchDocument(pageUrl));
      }
      return documents.flatMap((doc) => cardsFromDocument(doc).map(({ filters, handle }) => ({ filters, handle })));
    })();

    indexCache.set(key, promise);
    return promise;
  }

  function activeFilters() {
    const active = new Map();
    const params = new URL(location.href).searchParams;
    params.forEach((value, param) => {
      if (!param.startsWith('filter.')) return;
      if (!active.has(param)) active.set(param, []);
      active.get(param).push(value);
    });
    return active;
  }

  function matches(card, filters, ignoredParam = '') {
    for (const [param, activeValues] of filters) {
      if (param === ignoredParam || !card.filters[param]) continue;
      if (!activeValues.some((value) => card.filters[param].includes(value))) return false;
    }
    return true;
  }

  function updateCounts(index, filters) {
    document.querySelectorAll('form.filter-form li.tag').forEach((tag) => {
      const input = tag.querySelector('input[name^="filter."]');
      const count = tag.querySelector('[data-ll-filter-count]');
      if (!input || !count) return;

      const total = index.reduce((sum, card) => {
        if (!matches(card, filters, input.name)) return sum;
        return sum + ((card.filters[input.name] || []).includes(input.value) ? 1 : 0);
      }, 0);
      if (count.textContent !== String(total)) count.textContent = String(total);
      tag.classList.toggle('hide', total === 0 && !input.checked);
    });
  }

  function updateVisibleCards(filters) {
    cardsFromDocument(document).forEach((card) => {
      card.element.dataset.llVariantFilterHidden = String(!matches(card, filters));
    });
    document.dispatchEvent(new CustomEvent('ll:material-variant-filtered'));
  }

  async function update() {
    const filters = activeFilters();
    updateVisibleCards(filters);
    try {
      updateCounts(await loadCollectionIndex(), filters);
    } catch (error) {
      console.error(error);
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
  addEventListener('popstate', schedule);
  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();
